import { useMemo } from "react";

import { CaseQueueColumnSchemas } from "src/cases/queues/types";
import {
  getDefaultCaseColumnOrder,
  reconcileCaseColumnOrder,
} from "src/cases/queues/utils";
import { usePersistedColumnOrder } from "src/hooks/useColumnOrder";
import { CasesPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const STORAGE_KEY = "caseColumnOrder";

type UseCaseColumnOrderReturn = {
  orderedColumnIds: string[];
  hasCustomOrder: boolean;
  setOrder: (columnIds: string[]) => void;
  resetOrder: () => void;
};

/**
 * Personal, per-browser column order for the case list of a single queue.
 * Overlays a localStorage override on top of the queue's default column
 * order; falls back to that default when no override is stored.
 */
export const useCaseColumnOrder = (
  queueId: string | undefined,
  columnSchemas: CaseQueueColumnSchemas,
): UseCaseColumnOrderReturn => {
  const { wsId } = useParamsDecode<CasesPageParams>();

  const defaultOrder = useMemo(
    () => getDefaultCaseColumnOrder(columnSchemas),
    [columnSchemas],
  );

  return usePersistedColumnOrder({
    storageKey: STORAGE_KEY,
    identifier: `${wsId}|${queueId}`,
    defaultOrder,
    reconcile: reconcileCaseColumnOrder,
  });
};
